import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { createPortal } from "react-dom";
import { Avatar, Button, Table, Tag, Tooltip, Typography } from "antd";
import CustomerCareDrawer, { type DrawerIntent } from "./CustomerCareDrawer";
import { BUCKET_META, CONTACT_RESULT, FLAG_KIND, type CareState } from "./careStore";
import useTableColumnSettings, { SettingColumn } from "../../hooks/useTableColumnSettings";
import { ColumnSettingButton } from "../../components/TableColumnSetting";

import {
    CalendarClock,
    Crown,
    TriangleAlert,
    MessageCircle,
    Phone,
    Star,
} from "lucide-react";

interface Props {
    data: any[];
    /** Toàn bộ dòng đã tải — drawer lấy dữ liệu mới nhất theo id sau mỗi lần làm mới */
    allData?: any[];
    loading?: boolean;
    pagination?: {
        current: number;
        pageSize: number;
        total: number;
        onChange: (page: number, pageSize: number) => void;
    };
    onRefresh?: () => void;
    /** Chỗ trên toolbar của trang để đặt nút cài đặt cột; không truyền thì đặt ngay trên bảng */
    columnSettingSlot?: HTMLElement | null;
}

/** Hạn chăm sóc + các dấu hiệu cần chú ý (cảnh báo, hẹn lại, liên hệ không được) */
function CareDue({ care, extra }: { care: CareState; extra: any }) {
    const meta = BUCKET_META[care.bucket];
    const last = extra.attempts[0];
    return (
        <div className="space-y-1">
            <Tag color={meta.color} bordered={false} className="rounded-full !mr-0">
                {meta.label}{care.bucket === "overdue" ? ` ${care.lateDays} ngày` : ""}
            </Tag>
            {care.bucket !== "cared" && care.dueAt && (
                <div className="text-xs text-slate-400">Hạn: {dayjs(care.dueAt).format("DD/MM/YYYY")}</div>
            )}
            {extra.followUp && (
                <Tooltip title={extra.followUp.note || undefined}>
                    <div className={`text-xs flex items-center gap-1 ${care.followUpDue ? "text-violet-600 font-medium" : "text-slate-400"}`}>
                        <CalendarClock size={12} /> Hẹn lại {dayjs(extra.followUp.at).format("DD/MM")}
                    </div>
                </Tooltip>
            )}
            {care.openFlags.length > 0 && (
                <Tooltip title={care.openFlags.map((f) => `${FLAG_KIND[f.kind as keyof typeof FLAG_KIND]}: ${f.note}`).join(" · ")}>
                    <div className="text-xs text-red-500 font-medium flex items-center gap-1">
                        <TriangleAlert size={12} /> {care.openFlags.length} cảnh báo chưa xử lý
                    </div>
                </Tooltip>
            )}
            {care.lowCsat && care.openFlags.length === 0 && (
                <div className="text-xs text-red-500 flex items-center gap-1"><TriangleAlert size={12} /> CSAT thấp</div>
            )}
            {care.surveyPending && (
                <div className={`text-xs ${care.surveyRemind ? "text-amber-600 font-medium" : "text-purple-500"}`}>
                    {care.surveyRemind ? "Chưa đánh giá — cần nhắc" : "Đã gửi link khảo sát"} ({(extra.surveySends || []).length} lần)
                </div>
            )}
            {last && care.bucket !== "cared" && (
                <div className="text-xs text-slate-400">
                    {CONTACT_RESULT[last.result as keyof typeof CONTACT_RESULT].label}
                    {care.failedAttempts > 1 ? ` (${care.failedAttempts} lần)` : ""} · {dayjs(last.at).format("DD/MM HH:mm")}
                </div>
            )}
        </div>
    );
}

export default function CustomerTable({ data, allData, loading, pagination, onRefresh, columnSettingSlot }: Props) {
    const [openDrawer, setOpenDrawer] = useState(false);
    const [customerId, setCustomerId] = useState<number | null>(null);
    const [snapshot, setSnapshot] = useState<any>(null);
    const [intent, setIntent] = useState<DrawerIntent>("view");
    // Lấy bản mới nhất theo id — giữ bản chụp lúc mở phòng khi dòng đã bị lọc khỏi danh sách
    const customer = useMemo(
        () => (allData || data).find((r) => r.id === customerId) || snapshot,
        [allData, data, customerId, snapshot]
    );

    const handleOpen = (record: any, next: DrawerIntent = "view") => {
        setCustomerId(record.id);
        setSnapshot(record);
        setIntent(next);
        setOpenDrawer(true);
    };

    const columns: SettingColumn<any>[] = [
        {
            title: "Khách hàng",
            dataIndex: "name",
            width: 260,
            render: (_, record) => (
                <div className="flex items-center gap-3">
                    <Avatar
                        size={44}
                        style={{
                            background: record.color,
                            color: "#2563eb",
                            fontWeight: 700,
                        }}
                    >
                        {record.avatar}
                    </Avatar>

                    <div>
                        <div className="flex items-center gap-1 font-semibold text-[15px] dark:text-white">
                            {record.name}

                            {record.csat !== null && record.csat >= 4.5 && (
                                <Tooltip title={`CSAT ${record.csat} sao`}>
                                    <Crown
                                        size={15}
                                        className="fill-amber-400 text-amber-400"
                                    />
                                </Tooltip>
                            )}
                        </div>

                        <div className="text-xs text-slate-400">
                            {record.code}
                        </div>
                    </div>
                </div>
            ),
        },

        {
            title: "Liên hệ",
            dataIndex: "phone",
            width: 150,
            render: (value) => value
                ? <Typography.Text copyable={{ tooltips: ["Sao chép số", "Đã sao chép"] }}>{value}</Typography.Text>
                : <span className="text-slate-400">—</span>,
        },

        {
            title: "Dịch vụ đã làm",
            dataIndex: "service",
            width: 240,
            render: (value, record) => (
                <div>
                    <Tooltip title={value}>
                        <div className="font-medium dark:text-white line-clamp-2">
                            {value}
                        </div>
                    </Tooltip>

                    {record.doctor && (
                        <div className="text-xs text-slate-400 mt-1">
                            {record.doctor}
                        </div>
                    )}
                </div>
            ),
        },

        {
            title: "Ngày làm",
            dataIndex: "date",
            width: 130,
        },

        {
            key: "care_due",
            title: "Hạn chăm sóc",
            width: 200,
            render: (_, record) => <CareDue care={record.care} extra={record.extra} />,
        },

        {
            title: "Trạng thái chăm sóc",
            dataIndex: "status",
            width: 200,
            render: (_, record) => {
                const cared = !!record.is_cared;
                const colors = cared
                    ? { bg: "#DCFCE7", text: "#16A34A", dot: "#22C55E" }
                    : { bg: "#FEF3C7", text: "#D97706", dot: "#F59E0B" };

                return (
                    <Tooltip
                        title={
                            cared && (record.cared_by_staff_name || record.cared_at)
                                ? `${record.cared_by_staff_name || ""}${record.cared_at ? ` — ${dayjs(record.cared_at).format("HH:mm DD/MM/YYYY")}` : ""}`
                                : undefined
                        }
                    >
                        <Tag
                            bordered={false}
                            className="rounded-full px-3 py-[4px]"
                            style={{ background: colors.bg, color: colors.text }}
                        >
                            <span className="flex items-center gap-2">
                                <span
                                    className="w-2 h-2 rounded-full"
                                    style={{ background: colors.dot }}
                                />
                                {record.status}
                            </span>
                        </Tag>
                    </Tooltip>
                );
            },
        },

        {
            title: "CSAT",
            dataIndex: "csat",
            width: 110,
            align: "center",
            render: (value) =>
                value !== null && value !== undefined ? (
                    <div className="flex items-center justify-center gap-1 text-amber-500">
                        <Star
                            size={15}
                            className="fill-amber-500"
                        />

                        {value}
                    </div>
                ) : (
                    <span className="text-slate-400">
                        Chưa ĐG
                    </span>
                ),
        },

        {
            key: "actions",
            title: "Thao tác",
            width: 110,
            align: "center",
            fixed: "right",
            lockVisible: true,
            render: (_, record) => (
                <div className="flex justify-center gap-2">

                    <Tooltip title="Gọi chăm sóc">
                        <Button
                        shape="circle"
                        icon={<Phone size={16} />}
                        disabled={!record.phone}
                        onClick={() => handleOpen(record, "call")}
                    />
                        </Tooltip>

                    <Tooltip title="Ghi chú chăm sóc">
                        <Button
                        shape="circle"
                        icon={<MessageCircle size={16} />}
                        onClick={() => handleOpen(record, "note")}
                    />
                        </Tooltip>

                </div>
            )
        }
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings("care_list", columns, { scrollX: 1100 });

    const tablePagination = pagination
        ? {
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            onChange: pagination.onChange,
            showTotal: (t: number, range: [number, number]) => `${range[0]}-${range[1]} / ${t}`,
        }
        : { pageSize: 10, showSizeChanger: true };

    return (
        <>
            {/* Desktop */}

            <div className="hidden lg:block">

                {columnSettingSlot
                    ? createPortal(<ColumnSettingButton {...columnSettingProps} />, columnSettingSlot)
                    : (
                        <div className="flex justify-end mb-2">
                            <ColumnSettingButton {...columnSettingProps} />
                        </div>
                    )}

                <Table
                    rowKey="key"
                    columns={tableColumns}
                    components={tableComponents}
                    tableLayout={tableLayout}
                    dataSource={data}
                    loading={loading}
                    rowClassName={(r: any) => (r.care?.warning ? "bg-red-50/60 dark:bg-red-950/20" : "")}
                    pagination={tablePagination}
                    scroll={{ x: scrollX }}
                    className="rounded-xl overflow-hidden"
                />

            </div>

            {/* Mobile */}

            <div className="space-y-4 lg:hidden">

                {data.map((item) => (
                    <div
                        key={item.key}
                        className="rounded-2xl border bg-white dark:bg-[#262626] dark:border-neutral-700 p-4 shadow-sm"
                    >
                        <div className="flex gap-3">

                            <Avatar
                                size={46}
                                style={{
                                    background: item.color,
                                    color: "#2563eb",
                                }}
                            >
                                {item.avatar}
                            </Avatar>

                            <div className="flex-1">

                                <div className="font-semibold dark:text-white flex items-center gap-1">
                                    {item.name}

                                    {item.csat !== null && item.csat >= 4.5 && (
                                        <Crown
                                            size={14}
                                            className="fill-amber-400 text-amber-400"
                                        />
                                    )}
                                </div>

                                <div className="text-xs text-slate-400">
                                    {item.code}
                                </div>

                                <div className="mt-3 text-sm">
                                    {item.phone}
                                </div>

                                <div className="mt-2 font-medium dark:text-white">
                                    {item.service}
                                </div>

                                {item.doctor && (
                                    <div className="text-xs text-slate-400">
                                        {item.doctor}
                                    </div>
                                )}

                                <div className="text-xs text-slate-400">
                                    {item.date}
                                </div>

                                <div className="mt-3">
                                    <CareDue care={item.care} extra={item.extra} />
                                </div>

                                <div className="flex justify-between items-center mt-4">

                                    <div className="flex items-center gap-1 text-amber-500">
                                        {item.csat !== null && item.csat !== undefined ? (
                                            <>
                                                <Star
                                                    size={15}
                                                    className="fill-amber-500"
                                                />
                                                {item.csat}
                                            </>
                                        ) : (
                                            <span className="text-slate-400">
                                                Chưa ĐG
                                            </span>
                                        )}
                                    </div>

                                    <div className="flex gap-2">

                                        <Tooltip title="Gọi chăm sóc">
                                            <Button
                                            shape="circle"
                                            icon={<Phone size={15} />}
                                            disabled={!item.phone}
                                            onClick={() => handleOpen(item, "call")}
                                        />
                                            </Tooltip>

                                        <Tooltip title="Ghi chú chăm sóc">
                                            <Button
                                            shape="circle"
                                            icon={<MessageCircle size={15} />}
                                            onClick={() => handleOpen(item, "note")}
                                        />
                                            </Tooltip>

                                    </div>

                                </div>

                            </div>

                        </div>
                    </div>
                ))}

            </div>

            <CustomerCareDrawer
                open={openDrawer}
                customer={customer}
                intent={intent}
                onClose={() => setOpenDrawer(false)}
                onUpdated={onRefresh}
            />
        </>
    );
}
